import React, { createContext, useContext } from "react";

import { useUserPreferences } from "utils/hooks";
import { useRoutePattern } from "utils/routes";
import { useEphemeralAppState } from "providers/WithEphemeralAppState";

import { CoreProps } from "./util";
import { Column } from "./ColumnSettingsMenu";

type GridContextValue = {
  columns: Column[];
  hiddenFields: ReadonlySet<string>;
  toggleHidden: (field: string) => void;
  // Remembered track widths for the current visible-column set, or undefined
  // when none are set (or a remembered value no longer matches the visible
  // columns). Held in memory only — resizes are not persisted across reloads.
  savedColumnWidths: string[] | undefined;
  setColumnWidths: (widths: string[]) => void;
};

export const GridContext = createContext<GridContextValue | null>(null);

export function useGridContext(): GridContextValue {
  const ctx = useContext(GridContext);
  if (!ctx) {
    throw new Error("useGridContext must be used within a Grid");
  }
  return ctx;
}

function useHiddenFields<T>(columns: CoreProps<T>["columns"]) {
  const routePattern = useRoutePattern();
  const colNames = columns.map((col) => col.field);
  const key = `grid-${routePattern}-${colNames.join(",")}`;
  const [hiddenCols, setHiddenCols] = useUserPreferences("gridHiddenColumns");
  const currHidden = new Set<string>(hiddenCols[key] as string[]);
  function toggleHidden(field: string) {
    const next = new Set(currHidden);
    if (next.has(field)) {
      next.delete(field);
    } else {
      next.add(field);
    }
    setHiddenCols({
      ...hiddenCols,
      [key]: Array.from(next),
    });
  }

  return [currHidden, toggleHidden] as const;
}

// Remembered track widths, kept in ephemeral (in-memory) app state so a resize
// survives navigation within the SPA but resets on reload. Keyed by the
// *visible* column signature (unlike hidden fields, which key on all columns)
// so the array always lines up positionally with the tracks GridImpl renders.
// On a different visible set the key differs and we fall back to defaults —
// mirroring how the session-only trackOverride resets when the columns change.
function useColumnWidths(visibleColNames: string[]) {
  const routePattern = useRoutePattern();
  const key = `grid-${routePattern}-${visibleColNames.join(",")}`;
  const [stored, setColumnWidths] = useEphemeralAppState<string[]>(key);
  // Ignore a stale value whose length no longer matches the visible set.
  const savedColumnWidths =
    stored && stored.length === visibleColNames.length ? stored : undefined;
  return [savedColumnWidths, setColumnWidths] as const;
}

function WithGridContext<T>({
  columns: allColumns,
  children,
}: {
  columns: CoreProps<T>["columns"];
  children: React.ReactNode;
}) {
  const [hiddenFields, toggleHidden] = useHiddenFields(allColumns);
  const columns = allColumns.filter((col) => col.width !== false);
  // Must match GridImpl's visibleColumns filter so the width key and array
  // length stay in sync with the rendered tracks.
  const visibleColNames = columns
    .filter((col) => !hiddenFields.has(col.field))
    .map((col) => col.field);
  const [savedColumnWidths, setColumnWidths] = useColumnWidths(visibleColNames);
  const value: GridContextValue = {
    columns,
    hiddenFields,
    toggleHidden,
    savedColumnWidths,
    setColumnWidths,
  };
  return <GridContext.Provider value={value}>{children}</GridContext.Provider>;
}

export default WithGridContext;
